import { useState, useEffect } from "react"
import {
  Plus, Trash2, Loader2, CheckCircle2, Clock,
  Globe, Rss, ExternalLink, AlertCircle,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { DataGrid, type DataGridColumn } from "@/components/ui/DataGrid"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import {
  useMarketSources,
  useAddMarketSource,
  useDeleteMarketSource,
  useMarketSchedule,
  useUpdateMarketSchedule,
  useMarketLogs,
  type MarketSource,
} from "@/hooks/useMarketAnalysis"

// ─── Constants ───

const SOURCE_TYPES = [
  { value: "Blog", label: "Blog" },
  { value: "News", label: "Portal de Notícias" },
  { value: "News/Gov", label: "Portal Governamental" },
  { value: "Concorrente", label: "Concorrente" },
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "Forum", label: "Fórum / Comunidade" },
  { value: "Associação", label: "Associação / Instituto" },
  { value: "Research", label: "Research / Analistas" },
  { value: "RSS", label: "RSS Feed" },
]

const LOCATIONS = [
  { value: "Brasil", label: "Brasil" },
  { value: "Latam", label: "Latam" },
  { value: "EUA", label: "EUA" },
  { value: "EU", label: "EU" },
  { value: "Global", label: "Global" },
]

const DAYS = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sab" },
]

const SOURCE_COLORS: Record<string, string> = {
  Blog: "#3b82f6",
  News: "#f59e0b",
  "News/Gov": "#ea580c",
  Concorrente: "#ef4444",
  LinkedIn: "#0a66c2",
  Forum: "#6b7280",
  "Associação": "#8b5cf6",
  Research: "#14b8a6",
  RSS: "#10b981",
}

// ─── Helpers ───

function formatDateTime(d: string) {
  if (!d) return "-"
  try {
    const dt = new Date(d)
    return dt.toLocaleDateString("pt-BR") + " " + dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  } catch { return d }
}

function TypeBadge({ type }: { type: string }) {
  const c = SOURCE_COLORS[type] || "#6b7280"
  return (
    <span
      className="inline-flex px-2 py-0.5 rounded-md border text-[11px] font-semibold"
      style={{ backgroundColor: `${c}20`, color: c, borderColor: `${c}30` }}
    >
      {type}
    </span>
  )
}

// ─── Main Component ───

export default function MarketAnalysisSettingsInline() {
  // ── Sources ──
  const { data: sources = [], isLoading: sourcesLoading } = useMarketSources()
  const addSource = useAddMarketSource()
  const deleteSource = useDeleteMarketSource()
  const [confirmDeleteSourceId, setConfirmDeleteSourceId] = useState<string | null>(null)
  const [newUrl, setNewUrl] = useState("")
  const [newName, setNewName] = useState("")
  const [newType, setNewType] = useState("Blog")
  const [newLocation, setNewLocation] = useState("Brasil")

  function handleAddSource() {
    if (!newUrl.trim()) return
    addSource.mutate(
      { url: newUrl.trim(), name: newName.trim() || newUrl.trim(), type: newType, location: newLocation },
      { onSuccess: () => { setNewUrl(""); setNewName(""); setNewType("Blog"); setNewLocation("Brasil") } }
    )
  }

  // ── DataGrid Columns ──
  const sourceColumns: DataGridColumn<MarketSource>[] = [
    {
      id: 'name',
      header: 'Nome',
      sortable: true,
      filterable: true,
      accessor: (row) => <span className="font-medium">{row.name}</span>,
      sortValue: (row) => row.name,
      filterValue: (row) => row.name,
    },
    {
      id: 'url',
      header: 'URL',
      sortable: true,
      filterable: true,
      accessor: (row) => (
        <a href={row.url} target="_blank" rel="noopener noreferrer"
          className="text-primary hover:underline text-xs flex items-center gap-1">
          {row.url.replace(/^https?:\/\/(www\.)?/, '').substring(0, 40)}
          <ExternalLink className="w-3 h-3 flex-shrink-0" />
        </a>
      ),
      sortValue: (row) => row.url,
      filterValue: (row) => row.url,
    },
    {
      id: 'type',
      header: 'Tipo',
      minWidth: '130px',
      sortable: true,
      filterable: true,
      accessor: (row) => <TypeBadge type={row.type} />,
      sortValue: (row) => row.type,
      filterValue: (row) => row.type,
    },
    {
      id: 'location',
      header: 'Localidade',
      minWidth: '100px',
      sortable: true,
      filterable: true,
      accessor: (row) => {
        const flags: Record<string, string> = {
          'Brasil': '\ud83c\udde7\ud83c\uddf7', 'Latam': '\ud83c\udf0e', 'EUA': '\ud83c\uddfa\ud83c\uddf8', 'EU': '\ud83c\uddea\ud83c\uddfa', 'Global': '\ud83c\udf10'
        }
        return (
          <span className="text-xs flex items-center gap-1">
            <span>{flags[row.location] || '\ud83c\udf10'}</span>
            {row.location || 'Global'}
          </span>
        )
      },
      sortValue: (row) => row.location || '',
      filterValue: (row) => row.location || '',
    },
    {
      id: 'actions',
      header: 'Ações',
      minWidth: '60px',
      sortable: false,
      accessor: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setConfirmDeleteSourceId(row.id) }}
          disabled={deleteSource.isPending}
          className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Remover fonte"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ]

  // ── Schedule ──
  const { data: schedule, isLoading: scheduleLoading } = useMarketSchedule()
  const updateSchedule = useUpdateMarketSchedule()
  const [enabled, setEnabled] = useState(false)
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5])
  const [time, setTime] = useState("07:00")
  const [scheduleSaved, setScheduleSaved] = useState(false)

  useEffect(() => {
    if (schedule) {
      setEnabled(schedule.enabled)
      setDays(schedule.days)
      setTime(schedule.time)
    }
  }, [schedule])

  function toggleDay(d: number) {
    setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort())
  }

  function handleSaveSchedule() {
    updateSchedule.mutate({ enabled, days, time }, {
      onSuccess: () => { setScheduleSaved(true); setTimeout(() => setScheduleSaved(false), 2000) },
    })
  }

  // ── Logs ──
  const { data: logs = [], isLoading: logsLoading } = useMarketLogs()

  return (
    <div className="space-y-0">

      {/* Section 1: Fontes de Informacao */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Fontes de Informacao
        </h3>
        <p className="text-xs text-muted-foreground/70">
          URLs de sites, blogs e portais que a IA deve priorizar na varredura de oportunidades.
        </p>

        {/* Add form */}
        <div className="grid grid-cols-12 gap-3 items-end p-4 rounded-lg bg-muted/30 border">
          <div className="col-span-12 sm:col-span-4">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">URL do Site *</label>
            <input
              type="url"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="https://tiinside.com.br"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/50"
            />
          </div>
          <div className="col-span-12 sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Nome da Fonte</label>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="TI Inside"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/50"
            />
          </div>
          <div className="col-span-6 sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Tipo</label>
            <select
              value={newType}
              onChange={e => setNewType(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {SOURCE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="col-span-6 sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Localidade</label>
            <select
              value={newLocation}
              onChange={e => setNewLocation(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {LOCATIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          <div className="col-span-12 sm:col-span-2">
            <button
              onClick={handleAddSource}
              disabled={!newUrl.trim() || addSource.isPending}
              className="w-full px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {addSource.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Adicionar
            </button>
          </div>
        </div>

        {/* Sources DataGrid */}
        <DataGrid<MarketSource>
          gridId="market-sources"
          data={sources}
          columns={sourceColumns}
          isLoading={sourcesLoading}
          emptyMessage="Nenhuma fonte configurada"
          emptyIcon={
            <div className="text-center">
              <Globe className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs mt-1 text-muted-foreground/60">Adicione URLs de sites, blogs e portais acima</p>
            </div>
          }
          pageSize={20}
        />
      </div>

      {/* Section 2: Agendamento de Varredura */}
      <div className="space-y-4 mt-8 pt-6 border-t">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Agendamento de Varredura
        </h3>

        {scheduleLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-muted/30 border space-y-5">
            {/* Enable toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Varredura automatica</p>
                <p className="text-xs text-muted-foreground mt-0.5">Executa a busca de oportunidades nos dias e horario configurados</p>
              </div>
              <button
                onClick={() => setEnabled(!enabled)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  enabled ? "bg-primary" : "bg-muted-foreground/30"
                )}
              >
                <span className={cn(
                  "inline-block h-4 w-4 rounded-full bg-white transition-transform",
                  enabled ? "translate-x-6" : "translate-x-1"
                )} />
              </button>
            </div>

            {/* Days */}
            <div className={cn(!enabled && "opacity-50 pointer-events-none")}>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Dias da Semana</label>
              <div className="flex gap-2 flex-wrap">
                {DAYS.map(d => (
                  <button
                    key={d.value}
                    onClick={() => toggleDay(d.value)}
                    className={cn(
                      "w-12 h-10 rounded-lg border text-sm font-medium transition-all",
                      days.includes(d.value)
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "bg-background hover:bg-accent border-border"
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time */}
            <div className={cn(!enabled && "opacity-50 pointer-events-none")}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Horario (BRT, UTC-3)</label>
              <input
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-32"
              />
            </div>

            {/* Info */}
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 flex gap-3">
              <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-400/80">
                A varredura sera executada automaticamente nos dias e horario configurados, no fuso horario de Brasilia (BRT, UTC-3).
              </p>
            </div>

            {/* Save */}
            <button
              onClick={handleSaveSchedule}
              disabled={updateSchedule.isPending}
              className="px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm rounded-lg font-medium disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {updateSchedule.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : scheduleSaved ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : null}
              {scheduleSaved ? "Salvo!" : "Salvar Agendamento"}
            </button>
          </div>
        )}
      </div>

      {/* Section 3: Log de Varredura */}
      <div className="space-y-4 mt-8 pt-6 border-t">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Log de Varredura
        </h3>

        {logsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Data/Hora</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Periodo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Conteudo Extraido</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">Gravacao</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  const isSuccess = log.status === "success"
                  return (
                    <tr key={log.id || i} className="border-t hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 text-muted-foreground tabular-nums text-xs">
                        {formatDateTime(log.timestamp)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border",
                          isSuccess
                            ? "bg-green-500/10 text-green-400 border-green-500/30"
                            : "bg-red-500/10 text-red-400 border-red-500/30"
                        )}>
                          {isSuccess ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                          {isSuccess ? "Sucesso" : "Falha"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {log.details?.date_from ? `${log.details.date_from} → ${log.details.date_to}` : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {log.details?.total_found != null ? `${log.details.total_found} itens encontrados` : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {log.details?.created != null ? (
                          <span className={cn(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
                            log.details.created > 0
                              ? "bg-green-500/10 text-green-400"
                              : "bg-yellow-500/10 text-yellow-400"
                          )}>
                            {log.details.created > 0 ? `${log.details.created} gravados` : "Nenhum novo"}
                          </span>
                        ) : (
                          <span className="text-red-400 text-xs">Falha</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
            <Clock className="w-6 h-6 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhuma execucao registrada</p>
            <p className="text-xs mt-1 text-muted-foreground/60">As execucoes aparecerao aqui apos a primeira busca</p>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDeleteSourceId}
        title="Excluir fonte de análise?"
        message="Esta fonte será removida do monitoramento. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteSource.isPending}
        onConfirm={async () => {
          if (!confirmDeleteSourceId) return
          await deleteSource.mutateAsync(confirmDeleteSourceId)
          setConfirmDeleteSourceId(null)
        }}
        onCancel={() => setConfirmDeleteSourceId(null)}
      />
    </div>
  )
}
